import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Label, Input, Text, Row, Col, Icon, Dropdown, Button } from '@evertel/web/ui'
import { DocumentSchemaSearchSelect } from './DocumentSchemaSearchSelect'
import { DocumentSearchFilter } from '@evertel/document'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { SessionService } from '@evertel/session/SessionService'
import { useService } from '@evertel/di'
import { APIDataDepartment, APIDataDocumentSchema } from '@evertel/types'

interface DocumentSearchFormProps {
    filter: DocumentSearchFilter
    onFilterChange: (updates: Partial<DocumentSearchFilter>) => void
    documentDepartments: APIDataDepartment[]
    documentSchemas: APIDataDocumentSchema[]
}

const DocumentSearchForm: React.FC<DocumentSearchFormProps> = ({
    filter,
    onFilterChange,
    documentDepartments,
    documentSchemas,
    ...otherProps
}) => {
    const sessionService = useService(SessionService, [])
    const departmentsAccessController = useService(DepartmentsAccessController, [])

    const [mode, setMode] = useState((filter?.state === 'archived' || filter?.by || filter?.schemaId) ? 'advanced' : 'basic')
    const minWidth = 200

    useEffect(() => {
        departmentsAccessController.init(sessionService.currentUserId)
    }, [])

    // Get departments where user is an executive
    const executiveDepartmentIds = useMemo(() => {
        return departmentsAccessController.departmentsWithRole('executive')
    }, [departmentsAccessController.departmentsAccess])

    // Get available departments based on ownership selection
    const availableDepartments = useMemo(() => {
        if (['any', 'orphan'].includes(filter.access)) {
            return documentDepartments.filter(dept => executiveDepartmentIds.includes(dept.id))
        }
        return documentDepartments
    }, [filter.access, documentDepartments, executiveDepartmentIds])

    // Check if user has executive access in any of the available departments
    const hasExecutiveAccess = useMemo(() => {
        return documentDepartments.some(dept => executiveDepartmentIds.includes(dept.id))
    }, [documentDepartments, executiveDepartmentIds])

    // Get ownership dropdown options
    const ownershipOptions = useMemo(() => {
        const options = [
            { label: 'Owned by me', value: 'owner' }
        ]

        if (hasExecutiveAccess) {
            options.push(
                { label: 'All Agency Docs', value: 'any' },
                { label: 'Orphaned Agency Docs', value: 'orphan' }
            )
        }

        return options
    }, [hasExecutiveAccess])

    // Handle department selection when ownership changes
    const handleOwnershipChange = useCallback((selected: any) => {
        const newAccess = selected?.value
        const updates: Partial<DocumentSearchFilter> = { access: newAccess }

        if (['any', 'orphan'].includes(newAccess)) {

            //Can only use these options in advanced mode
            setMode('advanced')

            // Filter to executive departments
            const executiveDocDepts = documentDepartments.filter(dept =>
                executiveDepartmentIds.includes(dept.id)
            )

            // If current department isn't in executive departments, select a new one
            const currentDeptId = filter.by?.id
            if (!currentDeptId || !executiveDepartmentIds.includes(currentDeptId)) {
                // Try to use selected department if it's in executive departments
                if (executiveDocDepts.some(dept => dept.id === sessionService.selectedDepartmentId)) {
                    updates.by = { type: 'department', id: sessionService.selectedDepartmentId }
                } else if (executiveDocDepts.length > 0) {
                    // Otherwise use first executive department
                    updates.by = { type: 'department', id: executiveDocDepts[0].id }
                }
            }
        }

        onFilterChange(updates)
    }, [filter.by?.id, documentDepartments, executiveDepartmentIds, sessionService.selectedDepartmentId])

    const toggleAdvanced = useCallback(() => {
        if (mode === 'basic') {
            setMode('advanced')
        } else {
            setMode('basic')
            if (filter.state !== 'published' || filter.by || filter.schemaId || ['all', 'orphan'].includes(filter.access)) {
                onFilterChange({
                    state: 'published',
                    by: null,
                    schemaId: null,
                    access: null
                })
            }
        }
    }, [filter, mode])

    return (
        <div className="py-2 px-3 mb-3 border rounded position-relative" {...otherProps}>
            <Row className="flex-wrap" id="advanced-search-section">
                <Col sm="4" className="pe-2 mb-2" style={{ minWidth: minWidth }}>
                    <Label text="Ownership">
                        <Dropdown
                            value={filter.access}
                            options={ownershipOptions}
                            onChange={handleOwnershipChange}
                            isClearable={true}
                        />
                    </Label>
                </Col>
                <Col sm="4" className="pe-2 mb-2" style={{ minWidth: minWidth, flexBasis: '50%' }}>
                    <Label text="Keyword">
                        <Input
                            value={filter.text}
                            placeholder="Search..."
                            onChange={(e) => {
                                onFilterChange({ text: e.target.value || '' })
                            }}
                        />
                    </Label>
                </Col>

                {(mode === 'advanced') &&
                    <>
                        <Col sm="4" className="pe-2 mb-2" style={{ minWidth: minWidth }}>
                            <Label text="Agency">
                                <Dropdown
                                    value={(filter.by?.type === 'department') ? filter.by?.id : null}
                                    options={availableDepartments.map(dept => ({
                                        label: dept.name,
                                        value: dept.id
                                    }))}
                                    onChange={(selected: any) => {
                                        if (selected?.value === filter.by?.id) return
                                        const newBy = selected ? { type: 'department' as const, id: selected.value } : null
                                        const updates: Partial<DocumentSearchFilter> = { by: newBy }
                                        // Only clear schema if switching to a department that doesn't own it
                                        if (newBy && filter?.schemaId) {
                                            const currentSchema = documentSchemas.find(s => s.id === filter.schemaId)
                                            if (currentSchema && currentSchema.departmentId !== newBy.id) {
                                                updates.schemaId = null
                                            }
                                        }
                                        onFilterChange(updates)
                                    }}
                                    isClearable={!['any', 'orphan'].includes(filter.access)}
                                />
                            </Label>
                        </Col>
                        <Col sm="4" className="pe-2 mb-2" style={{ minWidth: minWidth }}>
                            <Label text="Status">
                                <Dropdown
                                    value={filter.state}
                                    options={[
                                        { label: 'Published / Active', value: 'published' },
                                        { label: 'Archived / Inactive', value: 'archived' }
                                    ]}
                                    onChange={(selected: any) => {
                                        if (selected?.value === filter.state) return

                                        onFilterChange({ state: selected?.value })
                                    }}
                                    isClearable={false}
                                />
                            </Label>
                        </Col>
                        <Col sm="4" lg="3" className="pe-2 mb-2 flex-grow-0" style={{ minWidth: minWidth }}>
                            <Label text="Type">
                                <DocumentSchemaSearchSelect
                                    filter={filter}
                                    onFilterChange={onFilterChange}
                                    documentSchemas={documentSchemas}
                                    isClearable={true}
                                />
                            </Label>
                        </Col>
                    </>
                }
                <Button
                    color="link"
                    size="sm"
                    className="position-absolute top-0 end-0 px-2"
                    onClick={toggleAdvanced}
                    aria-expanded={mode === 'advanced'}
                    aria-controls="advanced-search-section"
                    title={mode === 'basic' ? undefined : 'Hide Advanced'}
                    aria-label={mode === 'basic' ? 'Show advanced search options' : 'Hide advanced search options'}
                >
                    {mode === 'basic' ? 'Advanced search' : <Icon name="xmark" />}
                </Button>
            </Row>
        </div>
    )
}

export { DocumentSearchForm }
